import { Component } from '@theme/component';
import { requestIdleCallback, debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { subscribeToBlogIndex } from '@theme/sl-blog-index';

/**
 * The blog listing — the article grid, the topic chips and Load more.
 *
 * Three things make this different from the theme's `PaginatedList`, which is why the blog does not
 * reuse it:
 *
 * 1. **`page` never reaches the browser URL.** `PaginatedList` pushes `?page=N` as the shopper
 *    scrolls (`assets/paginated-list.js`). On this page that is corrupting: `sl-blog-tag-index`
 *    runs its own `paginate ... by 250` over the same `blog.articles`, and `?page` shifts *every*
 *    paginate in a render — so a shared or reloaded `?page=2` URL would hand the tag index the
 *    tail of the blog instead of the whole of it. `page` is therefore only ever set on the fetch
 *    URL, never pushed.
 *
 * 2. **The server page and the reveal step are different sizes.** The server sends `per_page`
 *    articles (8); mobile shows 4. So the first Load more on mobile reveals the other 4 already in
 *    the DOM with no network at all, and only the next one fetches.
 *
 * 3. **The chips filter without asking the server what the options are.** Topic state lives in the
 *    URL as `/tagged/<category>+<topic>+<topic>`, which Shopify filters natively as AND. The counts
 *    come off the tag index, client-side, so chips and the heading update on the same frame as the
 *    click and only the grid waits for the network.
 *
 * THE INDEX CAN NARROW BUT NOT WIDEN. It was rendered for the URL this page loaded with, so it
 * holds exactly those articles. Adding topics is a subset and free; removing one of the topics that
 * was already in the URL at load needs a set the index does not have, and falls back to a full
 * navigation. That only happens on a cold load of a multi-topic URL — a shared link.
 *
 * The heading count and the total both come from `paginate.items`, which is the *filtered* total —
 * `blog.articles_count` always returns the whole blog and is wrong under a `/tagged/` URL.
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} grid - The grid; its element children are the cards.
 * @property {HTMLElement} counter - The "You've viewed X of Y" line.
 * @property {HTMLButtonElement} button - The Load more button.
 * @property {HTMLElement} [count] - The bracketed count in the heading.
 * @property {HTMLElement} [chips] - The chip region.
 * @property {HTMLElement} [chipList] - The chips themselves.
 * @property {HTMLElement} [chipsFade] - The fade over collapsed rows.
 * @property {HTMLButtonElement} [chipsToggle] - Show more / show less.
 * @property {HTMLTemplateElement} [chipTemplate] - One chip's markup.
 * @property {HTMLTemplateElement} [clearTemplate] - The clear-all chip's markup.
 * @property {HTMLElement} [empty] - The "nothing matches" note.
 * @property {HTMLElement} [more] - The counter + button block.
 * @property {HTMLInputElement} [searchInput] - The article search field.
 */

/** How many rows of chips stay visible before the fade. */
const CHIP_ROWS_DESKTOP = 3;
const CHIP_ROWS_MOBILE = 2;

/** Shopify's own search debounce, matched so the two feel the same. */
const SEARCH_DEBOUNCE_MS = 400;

/** Below this a query is too broad to be worth a round trip. */
const SEARCH_MIN_LENGTH = 2;

/** @extends {Component<Refs>} */
export class SlBlogComponent extends Component {
  /** The chip refs are guarded rather than required, so a template can leave the chips out. */
  /** @type {string[]} */
  requiredRefs = ['grid', 'counter', 'button'];

  /** How many cards are revealed right now. */
  #visible = 0;

  /** Articles per server page, and the size of one reveal on desktop. */
  #perPage = 8;

  /** The filtered total — `paginate.items`. */
  #total = 0;

  /** `paginate.pages`. */
  #pages = 1;

  /** The highest server page whose cards are in the DOM. */
  #loadedPage = 1;

  /** Page number -> parsed cards, filled by the idle prefetch. @type {Map<number, Element[]>} */
  #prefetched = new Map();

  /** True while a fetch is in flight, so a double press cannot double-append. */
  #busy = false;

  /** The tag index, once it is up. @type {import('@theme/sl-blog-index').SlBlogIndexComponent | null} */
  #index = null;

  /** @type {(() => void) | null} */
  #unsubscribe = null;

  #chipsExpanded = false;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** Last observed chip-region width, so re-collapsing cannot feed its own observer. */
  #lastWidth = 0;

  /** The live query, or '' when not searching. */
  #query = '';

  /** Search results paginate separately from the blog, so their own page cursor. */
  #searchPage = 1;
  #searchPages = 1;

  /** @type {((event: Event) => void) | null} */
  #debouncedSearch = null;

  /** @type {AbortController} */
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  // ------------------------------------------------------------------ setup

  #init() {
    this.#abort.abort();
    this.#abort = new AbortController();
    this.#prefetched = new Map();
    this.#busy = false;
    this.#chipsExpanded = false;
    this.#lastWidth = 0;

    this.#perPage = this.#number('perPage', 8);
    this.#total = this.#number('total', 0);
    this.#pages = this.#number('pages', 1);
    this.#loadedPage = this.#number('page', 1);

    // Mobile opens on half a page, per the design. Derived from the setting rather than hardcoded
    // to 4 so raising "articles per page" keeps the two steps in proportion.
    this.#visible = Math.min(this.#step, this.#cards.length);

    this.#render();
    this.#schedulePrefetch();

    // The index is a different section and may render either side of this one, so it is reached
    // through its module registry rather than through refs. The callback also fires again when the
    // idle backfill completes, which is what refreshes the counts on the unfiltered blog.
    this.#unsubscribe?.();
    this.#unsubscribe = subscribeToBlogIndex((index) => {
      this.#index = index;
      this.#renderChips();
    });

    this.#observeChips();

    // Debounced once per init rather than per keystroke, so the timer is actually shared.
    this.#debouncedSearch = debounce(() => this.#runSearch(), SEARCH_DEBOUNCE_MS);

    window.addEventListener('popstate', this.#onPopState, { signal: this.#abort.signal });
  }

  /**
   * @param {string} key - A camelCase dataset key.
   * @param {number} fallback
   * @returns {number}
   */
  #number(key, fallback) {
    const value = Number(this.dataset[key]);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  /** The cards, live. Read off the grid rather than a `ref="items[]"`: the base class refreshes
   *  refs through a MutationObserver, so straight after an append `this.refs.items` is still stale. */
  get #cards() {
    return Array.from(this.refs.grid.children);
  }

  /** One reveal: a full server page on desktop, half of one on mobile. */
  get #step() {
    return this.#isDesktop ? this.#perPage : Math.ceil(this.#perPage / 2);
  }

  get #isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  // -------------------------------------------------------------------- URL

  /** The tags in the current path, in URL order. */
  get #urlTags() {
    const match = window.location.pathname.match(/\/tagged\/([^/?#]+)/);
    if (!match) return [];
    return decodeURIComponent(match[1])
      .split('+')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  /** The tags that count as categories rather than topics. */
  get #categoryTags() {
    return new Set(
      (this.dataset.categoryTags ?? '')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    );
  }

  /**
   * The category — the URL's first tag, but only when that tag really is a category.
   *
   * Falling back to "whatever is first" broke the unfiltered blog: selecting a topic on
   * `/blogs/news` puts it first in the URL, it was then read as the category, and so it vanished
   * from the chip row while every other chip kept its whole-blog count. The chips promised numbers
   * the grid could not deliver.
   */
  get #category() {
    const first = this.#urlTags[0];
    return first && this.#categoryTags.has(first) ? first : '';
  }

  /** Everything after the category. */
  get #topics() {
    const tags = this.#urlTags;
    return this.#category ? tags.filter((tag) => tag !== this.#category) : tags;
  }

  /** The topics the index was rendered for. It cannot widen past these. */
  get #baseTopics() {
    return (this.dataset.baseTopics ?? '').split(/\s+/).filter(Boolean);
  }

  /** Topics selected beyond what the index already holds — what `counts()` must be asked about. */
  get #selection() {
    const base = new Set(this.#baseTopics);
    return new Set(this.#topics.filter((tag) => !base.has(tag)));
  }

  /**
   * @param {string[]} topics
   * @returns {URL} The same URL with a rebuilt `/tagged/` segment. Query stays, so `?view=preview`
   *   survives, and `page` is never introduced.
   */
  #urlFor(topics) {
    const url = new URL(window.location.href);
    const tags = [this.#category, ...topics].filter(Boolean);
    const base = url.pathname.replace(/\/tagged\/[^/?#]*/, '');
    url.pathname = tags.length ? `${base}/tagged/${tags.join('+')}` : base;
    url.searchParams.delete('page');
    return url;
  }

  // ----------------------------------------------------------------- render

  #render() {
    const cards = this.#cards;
    cards.forEach((card, index) => {
      if (card instanceof HTMLElement) card.hidden = index >= this.#visible;
    });

    this.#updateCounter();

    // Nothing left to reveal and nothing left to fetch.
    this.refs.button.hidden = Math.min(this.#visible, this.#total) >= this.#total;
  }

  #updateCounter() {
    const shown = Math.min(this.#visible, this.#total);
    const template = this.dataset.counterTemplate ?? '';
    this.refs.counter.textContent = template
      .replace('__SHOWN__', String(shown))
      .replace('__TOTAL__', String(this.#total));
  }

  // ------------------------------------------------------------------ chips

  /** True when a category or any topic is applied — what "clear all" would undo. */
  get #hasFilters() {
    return Boolean(this.#category) || this.#topics.length > 0;
  }

  /** Declarative `on:click="/clearAll"`. Always a full navigation: dropping the category widens
   *  past what the index holds, and the featured panel has to be re-rendered for the new one. */
  clearAll() {
    window.location.assign(this.dataset.blogUrl || '/');
  }

  #renderChips() {
    const index = this.#index;
    const { chips, chipList, chipTemplate } = this.refs;
    if (!index || !chips || !chipList || !chipTemplate) return;

    const selection = this.#selection;
    const counts = index.counts(selection);
    // While searching the topics are reset, so nothing is drawn as selected.
    const selected = this.#searching ? new Set() : new Set(this.#topics);

    // `topics()` is already count-desc with alphabetical ties, so the order is stable between
    // renders. The category itself is never a chip — it is the layer above.
    const handles = index.topics().filter((handle) => handle !== this.#category);

    // Selected chips are hoisted to the front, which also guarantees collapsing a row can never
    // hide one of them.
    const ordered = [
      ...handles.filter((handle) => selected.has(handle)),
      ...handles.filter((handle) => !selected.has(handle) && (counts.get(handle) ?? 0) > 0),
    ];

    const fragment = document.createDocumentFragment();

    // First in the row so it is where the eye lands once filters are on, and so collapsing rows
    // can never hide the only way back out.
    if (this.#hasFilters && !this.#searching && this.refs.clearTemplate) {
      const clear = this.refs.clearTemplate.content.firstElementChild?.cloneNode(true);
      if (clear instanceof HTMLElement) fragment.append(clear);
    }

    for (const handle of ordered) {
      const chip = chipTemplate.content.firstElementChild?.cloneNode(true);
      if (!(chip instanceof HTMLElement)) continue;

      const isSelected = selected.has(handle);
      chip.dataset.handle = handle;
      chip.setAttribute('aria-pressed', String(isSelected));

      const label = chip.querySelector('.sl-blog__chip-label');
      const count = chip.querySelector('.sl-blog__chip-count');
      if (label) label.textContent = index.label(handle);
      // A selected chip's count is the result total, since every matching article carries it.
      if (count) count.textContent = `(${counts.get(handle) ?? 0})`;

      fragment.append(chip);
    }

    chipList.replaceChildren(fragment);
    chips.hidden = ordered.length === 0 && !this.#hasFilters;
    this.#lastWidth = Math.round(chips.getBoundingClientRect().width);
    this.#applyCollapse();
  }

  /** Collapse to the drawn row count, and only offer the toggle when something is actually cut. */
  #applyCollapse() {
    const { chipList, chipsToggle, chipsFade } = this.refs;
    if (!chipList || !chipsToggle) return;

    const chips = Array.from(chipList.children).filter(
      /** @returns {element is HTMLElement} */ (element) => element instanceof HTMLElement
    );
    if (!chips.length) return;

    // Measure with everything shown: hiding a chip rewraps the rest and invalidates the offsets.
    for (const chip of chips) chip.hidden = false;

    const maxRows = this.#isDesktop ? CHIP_ROWS_DESKTOP : CHIP_ROWS_MOBILE;
    const rowTops = [...new Set(chips.map((chip) => chip.offsetTop))].sort((a, b) => a - b);
    const cutoff = rowTops[maxRows];
    const overflows = cutoff !== undefined;

    if (overflows && !this.#chipsExpanded) {
      for (const chip of chips) if (chip.offsetTop >= cutoff) chip.hidden = true;
    }

    chipsToggle.hidden = !overflows;
    chipsToggle.setAttribute('aria-expanded', String(this.#chipsExpanded));
    chipsToggle.textContent = this.#chipsExpanded
      ? (this.dataset.showFewerLabel ?? '')
      : (this.dataset.showMoreLabel ?? '');

    if (chipsFade) chipsFade.hidden = !overflows || this.#chipsExpanded;
  }

  /** Re-collapse on a real width change only — hiding chips changes the region's height, and
   *  reacting to that would feed the observer its own output. */
  #observeChips() {
    this.#resizeObserver?.disconnect();
    const host = this.refs.chips;
    if (!host || typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver = new ResizeObserver((entries) => {
      const width = Math.round(entries[0]?.contentRect.width ?? 0);
      if (width === this.#lastWidth) return;
      this.#lastWidth = width;
      this.#applyCollapse();
    });
    this.#resizeObserver.observe(host);
  }

  // ----------------------------------------------------------------- search

  get #searching() {
    return this.#query.length > 0;
  }

  /** Declarative `on:submit`. The field searches as you type, so submitting is a no-op — but only
   *  once JS is running. Without it the form is a real GET to /search and still works. */
  submitSearch(event) {
    event.preventDefault();
  }

  /** Declarative `on:input`. */
  searchInput() {
    this.#debouncedSearch?.();
  }

  async #runSearch() {
    const value = this.refs.searchInput?.value.trim() ?? '';
    if (value === this.#query) return;

    const wasSearching = this.#searching;
    this.#query = value;

    if (!this.#searching) {
      // Back to browsing: rebuild for whatever the URL currently is.
      this.#setSearchingState(false);
      await this.#loadFiltered(new URL(window.location.href));
      return;
    }

    if (value.length < SEARCH_MIN_LENGTH) return;

    // Searching resets the topics: results are scoped to the category only, so leaving chips
    // selected would claim a narrowing that is not being applied. Done once, on entry.
    if (!wasSearching && this.#topics.length) {
      history.pushState({}, '', this.#urlFor([]).toString());
    }

    this.#setSearchingState(true);
    await this.#loadSearch(1, false);
  }

  /** @param {boolean} on */
  #setSearchingState(on) {
    this.classList.toggle('is-searching', on);
    const { chips } = this.refs;
    if (chips) chips.setAttribute('aria-disabled', String(on));
    if (!on) return;
    // Drop the pressed state so the dimmed row does not read as an active filter.
    this.#renderChips();
  }

  /**
   * @param {number} page
   * @param {boolean} append
   */
  async #loadSearch(page, append) {
    this.#busy = true;
    this.refs.grid.setAttribute('aria-busy', 'true');

    try {
      // `tag:<category>` keeps the search inside the category being browsed, the way the nav this
      // replaces did. The category is a handle and Shopify matches it against the tag.
      const term = this.#category ? `tag:${this.#category} ${this.#query}` : this.#query;
      // The form's own action, which Liquid rendered from `routes.search_url`. `Theme.routes` does
      // not carry a search URL in this theme, and a hardcoded '/search' would break the moment the
      // store serves a localised path.
      const action = this.refs.searchInput?.form?.getAttribute('action') || '/search';
      const url = new URL(action, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'article');
      if (page > 1) url.searchParams.set('page', String(page));

      const html = await sectionRenderer.getSectionHTML('search-results', false, url);
      if (this.#abort.signal.aborted || !this.#searching) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const list = doc.querySelector('#ResultsList');
      const items = Array.from(doc.querySelectorAll('.blog-post-item'));

      this.#searchPage = page;
      this.#searchPages = Number(list?.dataset.lastPage ?? 1) || 1;

      // The search grid emits `<li>`s of its own; rewrapping keeps one grid contract rather than
      // teaching the CSS about a second item shape.
      const wrapped = items.map((item) => {
        const cell = document.createElement('div');
        cell.className = 'sl-blog__item';
        cell.append(...Array.from(item.childNodes).map((n) => n.cloneNode(true)));
        return cell;
      });

      if (append) {
        this.refs.grid.append(...wrapped);
        this.#visible += this.#step;
      } else {
        this.refs.grid.replaceChildren(...wrapped);
        this.#visible = Math.min(this.#step, wrapped.length);
      }

      // Search has no filtered total of its own to trust, so the count is what came back.
      const shown = this.refs.grid.children.length;
      this.#total = this.#searchPages > this.#searchPage ? Math.max(this.#total, shown + 1) : shown;

      const isEmpty = this.refs.grid.children.length === 0;
      this.#showEmpty(isEmpty, true);
      this.#syncCount();
      this.#render();
    } catch (error) {
      console.error('sl-blog: the article search failed', error);
    } finally {
      this.#busy = false;
      this.refs.grid.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {boolean} isEmpty
   * @param {boolean} searching - Picks which of the two messages the note carries.
   */
  #showEmpty(isEmpty, searching) {
    const { empty, grid, more } = this.refs;
    grid.hidden = isEmpty;
    if (more) more.hidden = isEmpty;
    if (!empty) return;
    empty.hidden = !isEmpty;
    const label = searching ? empty.dataset.noResultsLabel : empty.dataset.emptyLabel;
    if (label) empty.textContent = label;
  }

  /** Declarative `on:click="/toggleChips"`. */
  toggleChips() {
    this.#chipsExpanded = !this.#chipsExpanded;
    this.#applyCollapse();
  }

  /**
   * Declarative `on:click="/toggleTopic"`. The event's `target` is proxied to the element carrying
   * the attribute, so it is the chip even when the click lands on the label or the icon.
   * @param {Event} event
   */
  async toggleTopic(event) {
    const chip = event.target;
    const handle = chip instanceof HTMLElement ? chip.dataset.handle : null;
    if (!handle) return;

    const topics = new Set(this.#topics);
    if (topics.has(handle)) topics.delete(handle);
    else topics.add(handle);

    await this.#applySelection([...topics]);
  }

  // --------------------------------------------------------------- filtering

  /** @param {string[]} topics */
  async #applySelection(topics) {
    const url = this.#urlFor(topics);

    // Widening past the index's own set — the server has to redo the query.
    if (this.#baseTopics.some((tag) => !topics.includes(tag))) {
      window.location.assign(url.toString());
      return;
    }

    history.pushState({}, '', url.toString());

    // Instant, off the index: chips, their counts and the heading. Only the grid waits.
    this.#renderChips();
    this.#applyLocalTotal();

    await this.#loadFiltered(url);
  }

  /** The new result total, straight off the index, so the heading does not wait for the network. */
  #applyLocalTotal() {
    if (!this.#index) return;
    this.#total = this.#index.matchCount(this.#selection);
    this.#syncCount();
  }

  /** Writes the bracketed count in the heading. Every path that moves `#total` ends here — the
   *  server's answer after a fetch, not only the index's instant guess, or clearing a search would
   *  leave the heading showing the topic-filtered total it had before. */
  #syncCount() {
    if (this.refs.count) this.refs.count.textContent = `(${this.#total})`;
    this.#updateCounter();
  }

  /** @param {URL} url */
  async #loadFiltered(url) {
    this.#busy = true;
    this.refs.grid.setAttribute('aria-busy', 'true');
    this.#prefetched = new Map();

    try {
      const items = await this.#fetchItems(url, (host) => {
        this.#total = Number(host?.dataset.total ?? this.#total) || 0;
        this.#pages = Number(host?.dataset.pages ?? 1) || 1;
      });
      if (this.#abort.signal.aborted) return;

      this.#loadedPage = 1;
      this.#visible = Math.min(this.#step, items.length);

      this.refs.grid.replaceChildren(...items.map((item) => item.cloneNode(true)));

      this.#showEmpty(items.length === 0, false);
      this.#syncCount();
      this.#render();
      this.#schedulePrefetch();
    } catch (error) {
      console.error('sl-blog: could not apply the topic filter', error);
    } finally {
      this.#busy = false;
      this.refs.grid.removeAttribute('aria-busy');
    }
  }

  #onPopState = () => {
    // Back and forward can widen past the index just as a chip can.
    if (this.#baseTopics.some((tag) => !this.#topics.includes(tag))) {
      window.location.reload();
      return;
    }
    this.#renderChips();
    this.#applyLocalTotal();
    this.#loadFiltered(new URL(window.location.href)).catch(() => {
      // Already reported by #loadFiltered.
    });
  };

  // ------------------------------------------------------------------ action

  /**
   * Declarative `on:click="/loadMore"`.
   *
   * The control is an `<a href="?page=N">` so the crawler has a path through the blog, which
   * means the click has to be swallowed here or the browser would navigate away instead of
   * appending. `component.js` passes the event as the last argument; it is optional only so a
   * direct call from a test or the console still works.
   *
   * @param {Event} [event]
   */
  async loadMore(event) {
    event?.preventDefault();

    if (this.#busy) return;

    // Search results paginate on their own cursor, not the blog's.
    if (this.#searching) {
      if (this.#visible < this.#cards.length) {
        this.#visible += this.#step;
        this.#render();
        return;
      }
      if (this.#searchPage >= this.#searchPages) return;
      await this.#loadSearch(this.#searchPage + 1, true);
      return;
    }

    // Still cards in the buffer — reveal them, no network.
    if (this.#visible < this.#cards.length) {
      this.#visible += this.#step;
      this.#render();
      this.#schedulePrefetch();
      return;
    }

    if (this.#loadedPage >= this.#pages) return;

    const next = this.#loadedPage + 1;
    this.#busy = true;
    this.refs.grid.setAttribute('aria-busy', 'true');

    try {
      const cards = this.#prefetched.get(next) ?? (await this.#fetchPage(next));
      if (!cards?.length) return;

      this.refs.grid.append(...cards.map((card) => card.cloneNode(true)));
      this.#loadedPage = next;
      this.#visible += this.#step;
      this.#render();
      this.#schedulePrefetch();
    } finally {
      this.#busy = false;
      this.refs.grid.removeAttribute('aria-busy');
    }
  }

  // ------------------------------------------------------------------ fetch

  /** Pull the page after the last one loaded while the main thread is idle, so the next press
   *  lands on the buffer instead of on the network. */
  #schedulePrefetch() {
    const next = this.#loadedPage + 1;
    if (next > this.#pages || this.#prefetched.has(next)) return;

    // Only worth it once the shopper has reached the end of what is already in the DOM.
    if (this.#visible < this.#cards.length) return;

    requestIdleCallback(() => {
      this.#fetchPage(next).catch(() => {
        // A failed prefetch is not an error the shopper needs: the press-time fetch will retry
        // and report for itself.
      });
    });
  }

  /**
   * @param {number} page
   * @returns {Promise<Element[]>}
   */
  async #fetchPage(page) {
    const cached = this.#prefetched.get(page);
    if (cached) return cached;

    // `page` goes on the fetch URL only — see the note at the top of this file.
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));

    try {
      const items = await this.#fetchItems(url);
      if (items.length) this.#prefetched.set(page, items);
      return items;
    } catch (error) {
      console.error(`sl-blog: could not load page ${page}`, error);
      return [];
    }
  }

  /**
   * @param {URL} url
   * @param {(host: HTMLElement | null) => void} [readHost] - Given the response's own component
   *   element, so the caller can take the authoritative total and page count from the server.
   * @returns {Promise<Element[]>}
   */
  async #fetchItems(url, readHost) {
    const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
    if (this.#abort.signal.aborted) return [];

    // Parsing a detached document, not reaching into the live page — the cards have to be read out
    // of the response somehow, and `this.refs` cannot see a document that is not attached.
    const doc = new DOMParser().parseFromString(html, 'text/html');
    readHost?.(doc.querySelector('sl-blog-component'));
    return Array.from(doc.querySelectorAll('.sl-blog__item'));
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('sl-blog: the section-id attribute is required');
    return id;
  }
}

if (!customElements.get('sl-blog-component')) {
  customElements.define('sl-blog-component', SlBlogComponent);
}
